import React, { useState, useEffect } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { db } from '../firebase';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { useParams, useNavigate } from 'react-router-dom';
import '../styles/EditArticle.css';

const EditChapter = () => {
  const { chapterId } = useParams(); // Get chapterId from URL
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [guideChapterName, setGuideChapterName] = useState('');
  const [metaTitle, setMetaTitle] = useState(''); // Meta title state
  const [metaDescription, setMetaDescription] = useState(''); // Meta description state
  const [sections, setSections] = useState([]);
  const [newSection, setNewSection] = useState({ title: '', content: '' });

  useEffect(() => {
    const fetchOrCreateChapter = async () => {
      if (!chapterId) {
        console.warn("❌ No chapterId found. Redirecting to a new chapter.");
        navigate('/flipbook');
        return;
      }

      try {
        setLoading(true);
        const chapterRef = doc(db, 'GuideChapter', chapterId);
        const chapterSnap = await getDoc(chapterRef);

        if (chapterSnap.exists()) {
          const data = chapterSnap.data();
          setGuideChapterName(data.guideChapterName || '');
          setMetaTitle(data.metaTitle || ''); // Load meta title
          setMetaDescription(data.metaDescription || ''); // Load meta description
          setSections(data.sections || []);
        } else {
          // 🔥 Create a new chapter if it doesn't exist
          await setDoc(chapterRef, {
            guideChapterName: 'New Chapter',
            metaTitle: '', // Default meta title
            metaDescription: '', // Default meta description
            sections: [],
          });

          setGuideChapterName('New Chapter');
          setMetaTitle('');
          setMetaDescription('');
          setSections([]);
        }
      } catch (error) {
        console.error('🔥 Firestore Error:', error);
        alert('Failed to load chapter. Please check your database connection.');
      } finally {
        setLoading(false);
      }
    };

    fetchOrCreateChapter();
  }, [chapterId, navigate]);

  const handleSaveChapter = async () => {
    if (!chapterId) return;

    try {
      const chapterRef = doc(db, 'GuideChapter', chapterId);
      await updateDoc(chapterRef, {
        guideChapterName,
        metaTitle,
        metaDescription,
        sections,
      });
      alert('Chapter saved successfully!');
    } catch (error) {
      console.error('Error saving chapter:', error);
      alert('Failed to save chapter.');
    }
  };

  const handleAddSection = () => {
    if (!newSection.title || !newSection.content) {
      alert('Please fill in both title and content.');
      return;
    }

    setSections([...sections, newSection]);
    setNewSection({ title: '', content: '' });
  };

  const handleDeleteSection = (index) => {
    setSections(sections.filter((_, i) => i !== index));
  };

  const handleSectionChange = (index, key, value) => {
    const updatedSections = sections.map((section, i) =>
      i === index ? { ...section, [key]: value } : section
    );
    setSections(updatedSections);
  };

  if (loading) {
    return <div>Loading... Please wait.</div>;
  }

  return (
    <div className="edit-article-page">
      <button className="back-button" onClick={() => navigate('/flipbook')}>
        &larr; Back to Dashboard
      </button>
      <h1>Edit Chapter</h1>

      <div className="chapter-name">
        <h2>Guide Chapter Name</h2>
        <input
          type="text"
          value={guideChapterName}
          onChange={(e) => setGuideChapterName(e.target.value)}
          placeholder="Enter Guide Chapter Name"
        />
      </div>

      <div className="meta-fields">
        <h2>Meta Information</h2>
        <div>
          <label>Meta Title:</label>
          <input
            type="text"
            value={metaTitle}
            onChange={(e) => setMetaTitle(e.target.value)}
            placeholder="Enter Meta Title"
          />
        </div>
        <div>
          <label>Meta Description:</label>
          <textarea
            value={metaDescription}
            onChange={(e) => setMetaDescription(e.target.value)}
            placeholder="Enter Meta Description"
          />
        </div>
      </div>

      <div className="sections">
        <h2>Sections</h2>
        {sections.map((section, index) => (
          <div key={index} className="section-item">
            <input
              type="text"
              value={section.title}
              onChange={(e) => handleSectionChange(index, 'title', e.target.value)}
              placeholder="Section Title"
            />
            <CKEditor
              editor={ClassicEditor}
              data={section.content}
              onChange={(event, editor) => {
                const data = editor.getData();
                handleSectionChange(index, 'content', data);
              }}
            />
            <button onClick={() => handleDeleteSection(index)} className="delete-button">
              Delete Section
            </button>
          </div>
        ))}
      </div>

      <div className="add-section">
        <h2>Add New Section</h2>
        <input
          type="text"
          value={newSection.title}
          onChange={(e) => setNewSection({ ...newSection, title: e.target.value })}
          placeholder="New Section Title"
        />
        <CKEditor
          editor={ClassicEditor}
          data={newSection.content}
          onChange={(event, editor) => {
            const data = editor.getData();
            setNewSection({ ...newSection, content: data });
          }}
        />
        <button onClick={handleAddSection} className="add-button">
          Add Section
        </button>
      </div>

      <button onClick={handleSaveChapter} className="save-button">
        Save Chapter
      </button>
    </div>
  );
};

export default EditChapter;